/**
 * The numeric keypad behind every numeric box in cafeteria mode (PROD-1278
 * feedback, 2026-09-11): its layouts and what a press does to the box's text.
 *
 * Why it exists: the Inventory page's count box asked iOS for a numeric
 * keyboard (`type="number" inputMode="numeric"`, pushed in dfe2fcea2) and the
 * iPad still raised the full numbers-and-punctuation keyboard. iPadOS offers
 * web content no compact digit pad; the pad Apple Books shows over its page
 * field is a native control. So on a touch device the box asks for NO system
 * keyboard and shows this pad instead — the iOS phone-pad layout from that
 * example, `1 2 3` / `4 5 6` / `7 8 9` / `✓ 0 ⌫`. The check is Done (Abner,
 * 2026-09-15): tapping outside also closes the pad, but users won't know that.
 *
 * Two layouts:
 *   counts        `NUMERIC_KEYPAD_ROWS` — digits only; a count is a
 *                 non-negative integer, so the box never holds `e`, `-`, `.`
 *                 or a letter, however the text arrived (a press, a hardware
 *                 keyboard, a paste, a scanner).
 *   temperatures  `NUMERIC_KEYPAD_DECIMAL_ROWS` — the same pad with `.`
 *                 bottom-left (`. 0 ⌫`) and Done as a full-width row beneath;
 *                 the text keeps digits, one point and at most one decimal
 *                 digit (readings persist as Decimal(5,1)).
 *
 * Pure: `NumericKeypad` renders the rows, the boxes apply the result. The
 * serve screen's on-screen keyboard (`virtual-keyboard.ts`) already knows what
 * a digit and a backspace do to a string, so this module reuses that.
 */

import { applyVirtualKey, type VirtualKey } from './virtual-keyboard'

/** The keys a box takes from the pad: one character (a digit or the point), backspace, or Done. */
export type NumericKeypadKey = Extract<VirtualKey, { kind: 'char' | 'backspace' | 'confirm' }>

/** One cell of the pad; null is an empty cell (none today, kept so a layout can leave a gap). */
export type NumericKeypadCell = NumericKeypadKey | null

function digits(chars: string): NumericKeypadKey[] {
  return Array.from(chars, (char) => ({ kind: 'char', char }))
}

const CONFIRM: NumericKeypadKey = { kind: 'confirm' }
const BACKSPACE: NumericKeypadKey = { kind: 'backspace' }
const POINT: NumericKeypadKey = { kind: 'char', char: '.' }

/** The count pad: `1 2 3` / `4 5 6` / `7 8 9` / `✓ 0 ⌫`. */
export const NUMERIC_KEYPAD_ROWS: ReadonlyArray<ReadonlyArray<NumericKeypadCell>> = [
  digits('123'),
  digits('456'),
  digits('789'),
  [CONFIRM, ...digits('0'), BACKSPACE],
]

/** The temperature pad: the count pad with `.` bottom-left and Done as its own full-width row. */
export const NUMERIC_KEYPAD_DECIMAL_ROWS: ReadonlyArray<ReadonlyArray<NumericKeypadCell>> = [
  digits('123'),
  digits('456'),
  digits('789'),
  [POINT, ...digits('0'), BACKSPACE],
  [CONFIRM],
]

/** Keep the digits, drop everything else: a typed `e` or `-`, a pasted `12 ea`, a decimal point. */
export function sanitizeCountText(text: string): string {
  return text.replace(/\D+/g, '')
}

/**
 * Keep the digits plus at most one decimal separator with at most one decimal
 * digit — a temperature persists as Decimal(5,1), so 48.0 / 41.5 are the
 * finest grain the log stores.
 */
export function sanitizeDecimalText(text: string): string {
  const cleaned = text.replace(/[^0-9.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  if (rest.length === 0) {
    return whole ?? ''
  }
  return `${whole ?? ''}.${rest.join('').slice(0, 1)}`
}

/** A count box's text after one press; Done leaves it alone (the box closes its pad). */
export function applyNumericKeypadKey(value: string, key: NumericKeypadKey): string {
  return key.kind === 'confirm' ? value : sanitizeCountText(applyVirtualKey(value, key))
}

/** A temperature box's text after one press; Done leaves it alone. */
export function applyDecimalKeypadKey(value: string, key: NumericKeypadKey): string {
  return key.kind === 'confirm' ? value : sanitizeDecimalText(applyVirtualKey(value, key))
}
